/**
 * The edit sheet's logic (#91): a draft of the entry's fields as the parent
 * sees them, the patch of just what changed, why Save waits, and the toast's
 * words. Pure, so the "only changed fields" rule is tested without a screen.
 *
 * Every comparison is in what the sheet shows, never in stored numbers: a
 * 90 ml bottle is 3.0433 oz, and 4200 g shows as 9 lb 4.2 oz but reads back
 * as 4201 g. An untouched field must never be sent.
 */
import {
  type BottleUnit,
  formatAmount,
  formatWeight,
  fromOz,
  toOz,
  type WeightUnit,
} from "@babia/checks";
import {
  DIAPER_LABEL,
  type DiaperKind,
  type DoseUnit,
  entryName,
  type EntryLike,
  medsSummary,
  NOTE_KIND_TITLE,
  type NoteKind,
  parseDose,
  parseWeightInput,
  roundDose,
  type WeightInput,
  weightInputOf,
} from "./entry-text.ts";
import { whenText } from "./revisions.ts";

/** The fields an edit sends (the server's `EntryEdit`): only the ones that changed. */
export interface EntryPatch {
  babyId?: string;
  at?: Date;
  endedAt?: Date;
  amountOz?: number;
  diaperKind?: DiaperKind;
  label?: string;
  note?: string;
  noteKind?: NoteKind;
  doseAmount?: number;
  doseUnit?: DoseUnit;
  weightGrams?: number;
}

/** The household's units: bottles, and where a weight's unit chips start. */
export interface EditUnits {
  bottle: BottleUnit;
  weight: WeightUnit;
}

/** Every field the sheet can show, in the sheet's terms; each type reads its own. */
export interface EntryDraft {
  babyId: string;
  at: Date;
  /** A finished nap's end; an open nap has none and gets none here. */
  endedAt: Date | null;
  /** A bottle's amount in the household's unit. */
  amount: number | null;
  diaperKind: DiaperKind | null;
  /** The medicine. */
  label: string;
  doseText: string;
  doseUnit: DoseUnit;
  noteKind: NoteKind;
  text: string;
  weight: WeightInput;
}

/** Leeway for "later than now": the sheet's clock ticks every 20 seconds. */
const NOW_GRACE_MS = 60_000;
const MINUTE_MS = 60_000;

/** A picked time past now, beyond the leeway for the sheet's clock. */
export function isLaterThanNow(d: Date, now: Date): boolean {
  return d.getTime() > now.getTime() + NOW_GRACE_MS;
}

/** Both times show the same minute. */
export function sameMinute(a: Date, b: Date): boolean {
  return Math.floor(a.getTime() / MINUTE_MS) === Math.floor(b.getTime() / MINUTE_MS);
}

/** The sheet's starting values: the entry as it was saved. */
export function draftOf(e: EntryLike, units: EditUnits): EntryDraft {
  return {
    babyId: e.babyId,
    at: e.at,
    endedAt: e.endedAt,
    amount: e.amountOz === null ? null : fromOz(e.amountOz, units.bottle),
    diaperKind: e.diaperKind,
    label: e.label ?? "",
    doseText: e.doseAmount === null ? "" : String(e.doseAmount),
    doseUnit: e.doseUnit ?? "ml",
    noteKind: e.noteKind ?? "general",
    text: e.note ?? "",
    weight: weightInputOf(e.weightGrams, units.weight),
  };
}

/** The weight typed in a weight note's boxes, if it reads as one. */
function draftGrams(d: EntryDraft): number | null {
  const parsed = parseWeightInput(d.weight);
  return "grams" in parsed ? parsed.grams : null;
}

/** Only the fields whose shown value differs from the saved entry's. */
export function patchOf(base: EntryLike, d: EntryDraft, units: EditUnits): EntryPatch {
  const patch: EntryPatch = {};
  if (d.babyId !== base.babyId) patch.babyId = d.babyId;
  if (!sameMinute(d.at, base.at)) patch.at = d.at;
  switch (base.type) {
    case "feed": {
      const saved = base.amountOz === null ? null : fromOz(base.amountOz, units.bottle);
      if (d.amount !== null && d.amount !== saved) patch.amountOz = toOz(d.amount, units.bottle);
      break;
    }
    case "diaper":
      if (d.diaperKind && d.diaperKind !== base.diaperKind) patch.diaperKind = d.diaperKind;
      break;
    case "sleep":
      if (base.endedAt && d.endedAt && !sameMinute(d.endedAt, base.endedAt))
        patch.endedAt = d.endedAt;
      break;
    case "meds": {
      const label = d.label.trim();
      if (label && label !== (base.label ?? "")) patch.label = label;
      const amount = parseDose(d.doseText);
      if (amount === null) break;
      if (amount !== base.doseAmount) patch.doseAmount = amount;
      if (d.doseUnit !== base.doseUnit) patch.doseUnit = d.doseUnit;
      break;
    }
    case "note": {
      if (d.noteKind !== (base.noteKind ?? "general")) patch.noteKind = d.noteKind;
      const text = d.text.trim();
      if (text !== (base.note ?? "")) patch.note = text;
      if (d.noteKind !== "weight") break;
      const grams = draftGrams(d);
      const unit = d.weight.unit;
      if (
        grams !== null &&
        (typeof base.weightGrams !== "number" ||
          formatWeight(grams, unit) !== formatWeight(base.weightGrams, unit))
      )
        patch.weightGrams = grams;
      break;
    }
  }
  return patch;
}

export const isEmptyPatch = (p: EntryPatch): boolean => Object.keys(p).length === 0;

/**
 * Why Save waits, or null. Times are checked only once they moved, so an
 * entry logged a moment ago never reads as "later than now".
 */
export function draftProblem(base: EntryLike, d: EntryDraft, now: Date): string | null {
  const startMoved = !sameMinute(d.at, base.at);
  const endMoved = !!(base.endedAt && d.endedAt && !sameMinute(d.endedAt, base.endedAt));
  if (startMoved && isLaterThanNow(d.at, now)) return "That time is later than now";
  if (base.type === "sleep" && base.endedAt && d.endedAt) {
    if (endMoved && isLaterThanNow(d.endedAt, now)) return "That wake-up is later than now";
    if ((startMoved || endMoved) && d.endedAt.getTime() < d.at.getTime())
      return "A nap can't end before it starts";
  }
  switch (base.type) {
    case "feed":
      return d.amount === null ? "Pick an amount" : null;
    case "meds":
      if (!d.doseText.trim())
        return base.doseAmount === null ? null : "A logged dose stays: type the amount";
      return parseDose(d.doseText) === null ? "Type the dose as a number, like 2.5" : null;
    case "note": {
      const text = d.text.trim();
      if (d.noteKind !== "weight") return text || d.noteKind === "bath" ? null : "Type a few words";
      const parsed = parseWeightInput(d.weight);
      if ("invalid" in parsed) return parsed.invalid;
      if ("empty" in parsed) {
        if (typeof base.weightGrams === "number") return "A weight keeps its number: type one";
        if (!text) return "Type the weight or a few words";
      }
      return null;
    }
    default:
      return null;
  }
}

/** The entry with the patch applied, as the server stores it: trimmed text, "" as none. */
export function withPatch<E extends EntryLike>(e: E, p: EntryPatch): E {
  const next = { ...e };
  if (p.babyId !== undefined) next.babyId = p.babyId;
  if (p.at !== undefined) next.at = p.at;
  if (p.endedAt !== undefined) next.endedAt = p.endedAt;
  if (p.amountOz !== undefined) next.amountOz = p.amountOz;
  if (p.diaperKind !== undefined) next.diaperKind = p.diaperKind;
  if (p.label !== undefined) next.label = p.label.trim();
  if (p.note !== undefined) next.note = p.note.trim() || null;
  if (p.noteKind !== undefined) next.noteKind = p.noteKind;
  if (p.doseAmount !== undefined) next.doseAmount = roundDose(p.doseAmount);
  if (p.doseUnit !== undefined) next.doseUnit = p.doseUnit;
  if (p.weightGrams !== undefined) next.weightGrams = p.weightGrams;
  return next;
}

const sameValue = (a: unknown, b: unknown) =>
  a instanceof Date && b instanceof Date ? a.getTime() === b.getTime() : a === b;

/** The entry already holds every value in the patch. */
export function holdsPatch(e: EntryLike, p: EntryPatch): boolean {
  const after = withPatch(e, p);
  return (Object.keys(p) as (keyof EntryPatch)[]).every((k) => sameValue(e[k], after[k]));
}

/**
 * A failed edit's rollback: each field that still shows the patch goes back
 * to `confirmed` (the server's last row). A field a newer write changed since
 * keeps that write's value.
 */
export function withoutPatch<E extends EntryLike>(e: E, p: EntryPatch, confirmed: EntryLike): E {
  const after = withPatch(e, p);
  const next: Record<string, unknown> = { ...(e as object) };
  for (const k of Object.keys(p) as (keyof EntryPatch)[])
    if (sameValue(e[k], after[k])) next[k] = confirmed[k];
  return next as E;
}

/**
 * The patch that puts the saved values back, or null when one of them was
 * empty: the server can add a weight's number or a dose, but not clear one.
 */
export function undoPatch(base: EntryLike, p: EntryPatch): EntryPatch | null {
  const undo: Record<string, unknown> = {};
  for (const k of Object.keys(p) as (keyof EntryPatch)[]) {
    const old = base[k];
    if (k === "note") undo.note = old ?? "";
    else if (old === null || old === undefined) return null;
    else undo[k] = old;
  }
  return undo as EntryPatch;
}

/** What the toast says changed: "Bottle for Ada moved to Leo, now 4 oz, at 1:50 am". */
export function editSummary(
  before: EntryLike,
  after: EntryLike,
  ctx: {
    unit: BottleUnit;
    weightUnit: WeightUnit;
    now: Date;
    babyName: (id: string) => string;
  },
): string {
  const when = (d: Date) => whenText(d, ctx.now);
  const values: string[] = [];
  const startMoved = !sameMinute(before.at, after.at);
  if (before.type === "sleep" && before.endedAt && after.endedAt) {
    const endMoved = !sameMinute(before.endedAt, after.endedAt);
    if (startMoved && endMoved) values.push(`${when(after.at)} to ${when(after.endedAt)}`);
    else if (startMoved) values.push(`from ${when(after.at)}`);
    else if (endMoved) values.push(`until ${when(after.endedAt)}`);
  }
  switch (before.type) {
    case "feed":
      if (after.amountOz !== null && after.amountOz !== before.amountOz)
        values.push(formatAmount(after.amountOz, ctx.unit));
      break;
    case "diaper":
      if (after.diaperKind && after.diaperKind !== before.diaperKind)
        values.push(DIAPER_LABEL[after.diaperKind].toLowerCase());
      break;
    case "meds":
      if (
        after.label !== before.label ||
        after.doseAmount !== before.doseAmount ||
        after.doseUnit !== before.doseUnit
      )
        values.push(medsSummary(after));
      break;
    case "note":
      if (after.noteKind !== before.noteKind)
        values.push(`a ${NOTE_KIND_TITLE[after.noteKind ?? "general"].toLowerCase()}`);
      if (typeof after.weightGrams === "number" && after.weightGrams !== before.weightGrams)
        values.push(formatWeight(after.weightGrams, ctx.weightUnit));
      break;
  }
  if (startMoved && !(before.type === "sleep" && before.endedAt))
    values.push(`at ${when(after.at)}`);
  const parts = [
    after.babyId === before.babyId ? null : `moved to ${ctx.babyName(after.babyId)}`,
    values.length ? `now ${values.join(", ")}` : null,
    after.note === before.note ? null : "with new text",
  ].filter(Boolean);
  const what = `${entryName(before)} for ${ctx.babyName(before.babyId)}`;
  return parts.length ? `${what} ${parts.join(", ")}` : `${what} saved`;
}
